import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ListTodo, Clock, DollarSign, AlertTriangle, CheckCircle, 
  MoreVertical, Search, Filter, RefreshCw, Calendar 
} from 'lucide-react';
import { formatCurrency, formatDate, getDaysUntil } from '@/lib/utils';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyQueuePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tierFilter, setTierFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: queueData, isLoading, refetch } = useQuery({
    queryKey: ['my-queue', tierFilter],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/queue?limit=100${tierFilter !== 'ALL' ? `&tier=${tierFilter}` : ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
  });

  const { data: stats } = useQuery({
    queryKey: ['queue-stats'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/queue/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    },
  });

  const claimMutation = useMutation({
    mutationFn: async (contractId) => {
      const token = localStorage.getItem('ccip_token');
      await axios.post(
        `${BACKEND_URL}/api/queue/${contractId}/claim`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['my-queue']);
      queryClient.invalidateQueries(['queue-stats']);
      toast.success('Contract claimed!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to claim contract');
    },
  });

  const getTierColor = (tier) => {
    const colors = {
      CRITICAL: 'bg-red-100 text-red-800 border-red-300',
      HIGH: 'bg-orange-100 text-orange-800 border-orange-300',
      MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      LOW: 'bg-gray-100 text-gray-700 border-gray-300',
    };
    return colors[tier] || colors.MEDIUM;
  };

  const getTierIcon = (tier) => {
    if (tier === 'CRITICAL') return <AlertTriangle className="w-4 h-4" />;
    return <Clock className="w-4 h-4" />;
  };

  const filteredQueue = queueData?.data?.filter((item) => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      item.contract.contract_number.toLowerCase().includes(search) ||
      item.account.name.toLowerCase().includes(search)
    );
  });

  return (
    <div className="p-8 space-y-6" data-testid="my-queue-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <ListTodo className="w-10 h-10 text-[#09409C]" />
            <span className="text-gradient-primary">My Queue</span>
          </h1>
          <p className="text-muted-foreground mt-2 text-lg">
            {queueData?.meta?.total || 0} contracts prioritized for your attention
          </p>
        </div>
        <Button
          onClick={() => refetch()}
          variant="outline"
          data-testid="refresh-queue-button"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-2 border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Critical</p>
                <p className="text-3xl font-bold text-red-700">{stats?.byTier?.critical || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-2 border-orange-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">High Priority</p>
                <p className="text-3xl font-bold text-orange-700">{stats?.byTier?.high || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-2 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">In Progress</p>
                <p className="text-3xl font-bold text-blue-700">{stats?.byStatus?.inProgress || 0}</p>
              </div>
              <ListTodo className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-2 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completed Today</p>
                <p className="text-3xl font-bold text-green-700">{stats?.byStatus?.completedToday || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by contract number or account name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={tierFilter} onValueChange={setTierFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Tiers</SelectItem>
                <SelectItem value="CRITICAL">Critical</SelectItem>
                <SelectItem value="HIGH">High</SelectItem>
                <SelectItem value="MEDIUM">Medium</SelectItem>
                <SelectItem value="LOW">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
      </Card>

      {/* Queue Items */}
      <div className="space-y-3">
        {isLoading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#09409C]" />
            <p className="text-muted-foreground mt-4">Loading queue...</p>
          </div>
        ) : filteredQueue && filteredQueue.length > 0 ? (
          filteredQueue.map((item) => {
            const daysToExp = getDaysUntil(item.contract.end_date);
            const isUrgent = daysToExp <= 30;

            return (
              <Card
                key={item.id}
                className={`hover:shadow-md transition-shadow cursor-pointer ${
                  item.status === 'IN_PROGRESS' ? 'border-2 border-blue-300 bg-blue-50' : ''
                } ${isUrgent ? 'border-l-4 border-l-red-500' : ''}`}
                data-testid={`queue-item-${item.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-3">
                      {/* Header */}
                      <div className="flex items-center gap-3">
                        <Badge className={`${getTierColor(item.priority_tier)} flex items-center gap-1 font-semibold`}>
                          {getTierIcon(item.priority_tier)}
                          {item.priority_tier}
                        </Badge>
                        {item.status === 'IN_PROGRESS' && (
                          <Badge className="bg-blue-600 text-white">In Progress</Badge>
                        )}
                        <span className="text-sm text-muted-foreground">
                          Priority Score: {item.priority_score?.toFixed(0)}
                        </span>
                      </div>

                      {/* Contract Info */}
                      <div>
                        <h3 className="text-xl font-bold text-[#09409C]">
                          {item.contract.contract_number}
                        </h3>
                        <p className="text-gray-700">{item.account.name}</p>
                        <Badge variant="outline" className="mt-1">
                          {item.account.customer_status}
                        </Badge>
                      </div>

                      {/* Metrics */}
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">Annual Value</p>
                          <p className="text-lg font-semibold">{formatCurrency(item.contract.annual_value)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Days to Expiration</p>
                          <p className={`text-lg font-semibold ${isUrgent ? 'text-red-600' : ''}`}>
                            {daysToExp} days
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Competitiveness</p>
                          <p className="text-lg font-semibold">
                            {item.contract.competitiveness_score || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 ml-4">
                      {item.status === 'PENDING' ? (
                        <Button
                          onClick={() => claimMutation.mutate(item.contract_id)}
                          disabled={claimMutation.isPending}
                          className="bg-gradient-to-r from-[#09409C] to-[#146CBB]"
                          data-testid={`claim-button-${item.id}`}
                        >
                          Claim
                        </Button>
                      ) : (
                        <Button variant="outline" disabled>
                          Claimed
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => navigate(`/contracts/${item.contract_id}`)}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <ListTodo className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-xl text-muted-foreground">Your queue is empty</p>
              <p className="text-sm text-muted-foreground mt-2">
                No contracts match your current filters
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
